import {Api, Currency, LogManager, ModelRow, StringUtil} from "@mcleod/core";
import {AutogenLayoutWireFundsProcessingDetail} from "./autogen/AutogenLayoutWireFundsProcessingDetail";
import {
    ChangeEvent,
    LookupModelSelectionEvent,
    LookupModelSearchEvent,
    DataSourceExecutionEvent,
    Layout, BlurEvent, Snackbar
} from "@mcleod/components";
import {FuelInterface} from "@mcleod/integrations";
import WireType from "./WireType";
import WireStatus from "./WireStatus";

const log = LogManager.getLogger("WireFundsProcessingDetail");
export class WireFundsProcessingDetail extends AutogenLayoutWireFundsProcessingDetail {

    override onLoad() {
        log.debug("WireFundsProcessingDetail loaded");
        // Called when the layout is loaded. Sets up tooltip for QuikQ Asset ID.
        this.textboxIfuelQuikqAssetId.quickInfoOverrideSearchMethod = async (layout: Layout, keyValue: any) => {
            const searchFilter = { asset_id: keyValue };
            await layout.mainDataSource.search(searchFilter);
        };
    }

    /**
     * Handles the onChange event for the Deduct switch.
     * Updates visibility and required status for deduction-related fields.
     * @param event ChangeEvent
     */
    switchDeductOnChange(event: ChangeEvent) {
        this.textboxDeductCodeId.visible = this.textboxDeductCodeId.required = this.switchDeduct.checked;
        this.textboxDeductStatus.visible = this.textboxDeductStatus.required = this.switchDeduct.checked;
        this.textboxDeductTotal.visible = this.switchDeduct.checked;
        this.textboxPayeeId.required = this.switchDeduct.checked;
        this.textboxServiceCharge.required = this.switchDeduct.checked;
        this.textboxDriverFeeAmt.required = this.switchDeduct.checked;
    }

    /**
     * Handles the onChange event for the Other Charge switch.
     * Updates visibility and required status for other charge-related fields.
     * @param event ChangeEvent
     */
    switchOtherChargeOnChange(event: ChangeEvent) {
        const otherChargeChecked = this.switchOtherCharge.checked;
        this.textboxChargeCodeId.setProps({ visible: otherChargeChecked, required: otherChargeChecked });
        this.textboxChargeAmt.setProps({ visible: otherChargeChecked, required: otherChargeChecked });
    }

    /**
     * Updates UI field visibility and required status based on wire code properties.
     * @param wireCode ModelRow containing wire code data
     */
    private updateWireCodeFieldVisibility(wireCode: ModelRow) {
        const wireType = wireCode?.get("wirecode.wire_type");
        const vendor = wireCode?.get("wirecode.fuel_interface_id");
        const isOrderRequiredInWireCode = wireCode?.get("wirecode.require_order");
        this.resetComponentsVisibility();

        if (wireCode !== null) {
            this.textboxSubaccountId.visible = vendor === FuelInterface.COMDATA || vendor === FuelInterface.FLEETONE;
            this.textboxIfuelQuikqAssetId.visible = vendor === FuelInterface.QUIKO && wireType === WireType.INTERACTIVE_CHECK;
            this.textboxWireContractTch.visible = vendor === FuelInterface.EFS;
            this.textboxFuelCardId.visible = wireType === WireType.ATM_CASH;

            const statusValue = this.textboxStatus?.selectedItem?.value ?? this.mainDataSource?.activeRow?.get?.("status") ?? null;
            const statusIsVoid = statusValue === WireStatus.VOID;
            const shouldRequireOrder = !statusIsVoid || isOrderRequiredInWireCode === 'Y';
            this.textboxOrderId.required = shouldRequireOrder;
            this.textboxMovementId.required = shouldRequireOrder;
        }
    }

    /**
     * Resets visibility for all wire code dependent UI components.
     */
    resetComponentsVisibility() {
        this.textboxFuelCardId.visible = false;
        this.textboxWireContractTch.visible = false;
        this.textboxIfuelQuikqAssetId.visible = false;
        this.textboxSubaccountId.visible = false;
    }

    /**
     * Handles the onLookupModelSelection event for the Wirecode textbox.
     * Updates UI and data model fields, applies deduction and other charge logic.
     * @param event LookupModelSelectionEvent
     */
    async textboxWirecodeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (!event.hasSelection()) {
            return;
        }
        const wireCode = event.selection;
        this.resetComponentsVisibility();
        this.mainDataSource.activeRow.set("wirecode.fuel_interface_id", wireCode?.get("wirecode.fuel_interface_id"));
        this.updateWireCodeFieldVisibility(wireCode);

        this.updateFees(wireCode?.get("wirecode.id"));
        this.updateFieldData(wireCode);
        await this.applyDeductionFields(wireCode);
        await this.applyOtherChargeFields(wireCode);

        this.showHideFieldsBySelectedWireType();
    }

    /**
     * Updates the description and status fields in the data model from the wire code.
     * @param wireCode ModelRow containing wire code data
     */
    private updateFieldData(wireCode: ModelRow) {
        const description = wireCode?.get("wirecode.descr");
        const status = wireCode?.get("wirecode.deduct_status");
        this.sourceUnpostedWire.activeRow.set({
            descr: description,
            status: status
        });
    }

    /**
     * Applies deduction-related logic: toggles, fields, lookup, and status.
     * @param wireCode ModelRow containing wire code data
     */
    private async applyDeductionFields(wireCode: ModelRow) {
        const createDeduction = wireCode?.get("wirecode.create_deduction");
        this.switchDeduct.checked = createDeduction === true || createDeduction === 'Y';

        const deductCodeId = createDeduction ? wireCode?.get("wirecode.deduct_code_id") : "";
        this.sourceUnpostedWire.activeRow.set("deduct_code_id", deductCodeId);
        const deductStatus = createDeduction ? wireCode?.get("wirecode.deduct_status") : "";
        this.sourceUnpostedWire.activeRow.set("deduct_status", deductStatus);

        if (!deductCodeId) {
            return;
        }

        const deductCodeLookupData = wireCode.getLookupModelData("wirecode.deduct_code_id");
        if (!Array.isArray(deductCodeLookupData) || deductCodeLookupData.length === 0 || !deductCodeLookupData[0]) {
            log.error(`Deduct code lookup data missing for code ID: ${deductCodeId}`);
            return;
        }

        const deductCodeShortDesc = deductCodeLookupData[0].get("short_desc");
        if (!deductCodeShortDesc) {
            log.error(`Short description missing for deduct code ID: ${deductCodeId}`);
        }

        this.sourceUnpostedWire.activeRow.setValueAndLookupModelData(
            "deduct_code_id",
            deductCodeId,
            { id: deductCodeId, short_desc: deductCodeShortDesc }
        );
    }

    /**
     * Applies other charge-related logic: toggles, fields, lookup, and charge amount calculation.
     * @param wireCode ModelRow containing wire code data
     */
    private async applyOtherChargeFields(wireCode: ModelRow) {
        const createOtherCharge = wireCode?.get("wirecode.create_other_charge");
        this.switchOtherCharge.checked = createOtherCharge === true || createOtherCharge === 'Y';

        const chargeCodeId = createOtherCharge ? wireCode?.get("wirecode.charge_code_id") : "";
        this.sourceUnpostedWire.activeRow.set("charge_code_id", chargeCodeId);

        // Set charge amount
        const inclProviderFee = wireCode?.get("wirecode.incl_provider_fee");
        const checkAmount = this.activeRow.getCurrency("check_amount");
        const serviceCharge = this.activeRow.getCurrency("service_charge");
        if (this.shouldIncludeProviderFee(inclProviderFee, checkAmount, serviceCharge)) {
            const chargeAmt = await checkAmount.add(serviceCharge);
            this.sourceUnpostedWire.activeRow.set("charge_amt", chargeAmt);
        } else {
            this.sourceUnpostedWire.activeRow.set("charge_amt", checkAmount);
        }

        // If create other charge is not set or charge code ID is missing, skip further processing
        if (!createOtherCharge || !chargeCodeId) {
            return;
        }

        const chargeCodeLookupData = wireCode.getLookupModelData("wirecode.charge_code_id");
        if (!Array.isArray(chargeCodeLookupData) || chargeCodeLookupData.length === 0 || !chargeCodeLookupData[0]) {
            log.error(`Charge code lookup data missing for code ID: ${chargeCodeId}`);
            return;
        }

        const chargeCodeShortDesc = chargeCodeLookupData[0].get("short_desc");
        if (!chargeCodeShortDesc) {
            log.error(`Short description missing for charge code ID: ${chargeCodeId}`);
        }

        const chargeCodeDescr = Array.isArray(chargeCodeLookupData) && chargeCodeLookupData[0]
            ? chargeCodeLookupData[0].get("descr")
            : "";

        this.sourceUnpostedWire.activeRow.setValueAndLookupModelData(
            "charge_code_id",
            wireCode.get("wirecode.charge_code_id"),
            { id: wireCode.get("wirecode.charge_code_id"), descr: chargeCodeDescr }
        );
    }

    /**
     * Handles the beforeLookupModelSearch event for the Movement ID textbox.
     * Sets up search filters based on Order ID.
     * @param event LookupModelSearchEvent
     */
    async textboxMovementIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.textboxOrderId.text?.length < this.textboxOrderId.lookupModelMinChars) {
            event.preventDefault();
        } else {
            event.filter.order_id = this.textboxOrderId.text;
            event.filter.lm_search = this.textboxOrderId.text;
        }
    }

    /**
     * Handles the onLookupModelSelection event for the Order ID textbox.
     * Enables/disables Movement ID field and updates lookup model data.
     * @param event LookupModelSelectionEvent
     */
    textboxOrderIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const selection = event.selection;
        this.textboxMovementId.enabled = event.hasSelection();
        const moveId = event.getValueFromSelectedRow?.("id") ?? null;
        this.activeRow.setValueAndLookupModelData("movement_id", moveId, moveId ? event.selection : null);
        const driverId = event.getValueFromSelectedRow?.("equipment_item.equipment_id") ?? null;
        const payeeName = event.getValueFromSelectedRow?.("payee.name") ?? null;
        const payeeLmData = new ModelRow("lme/dispatch/settlement-payee", false, { name: payeeName, id: driverId });
        this.activeRow.setValueAndLookupModelData("payee_id", driverId, driverId ? payeeLmData : null);
        const driverLmData = new ModelRow("lme/dispatch/asset/driver-only", false,
            { first_name: selection?.get("driver.first_name") ?? null, name: selection?.get("driver.name") ?? null, id: driverId });
        this.activeRow.setValueAndLookupModelData("driver_id", driverId, driverId ? driverLmData : null);
    }

    /**
     * Handles the afterExecution event for the Unposted Wire data source.
     * Updates wire code fields and Movement ID requirements.
     * @param event DataSourceExecutionEvent
     */
    sourceUnpostedWireAfterExecution(event: DataSourceExecutionEvent) {
        this.updateWireCodeFieldVisibility(this.mainDataSource.activeRow);
        this.textboxMovementId.enabled = this.textboxMovementId.required = this.textboxOrderId.hasText();
    }

    /**
     * Handles the beforeExecution event for the Unposted Wire data source.
     * Updates fees if there are no validation warnings.
     * @param event DataSourceExecutionEvent
     */
    sourceUnpostedWireBeforeExecution(event: DataSourceExecutionEvent) {
        if (!this.activeRow) {
            return;
        }
        if (StringUtil.isEmptyString(this.textboxDriverFeeAmt.validationWarning)
            && StringUtil.isEmptyString(this.textboxServiceCharge.validationWarning)) {
            this.updateFees(this.activeRow.get("wirecode.id"));
        }
    }

    textboxStatusOnChange(event: ChangeEvent) {
        this.updateWireCodeFieldVisibility(this.mainDataSource.activeRow);
    }

    textboxPayeeIdOnBlur(event: BlurEvent) {
        const payeeStatus = this.textboxPayeeId.lookupModelData?.[0]?.get?.("status") ?? null;
        if ('N' === payeeStatus) {
            Snackbar.showWarningSnackbar("Warning! Payee status is 'New'. Status must be changed to 'Active'" +
                " in order to process this record in Interactive Settlements.");
        }
    }

    /**
     * Determines if provider fee should be included in charge amount calculation.
     * @param inclProviderFee Indicates if provider fee should be included
     * @param checkAmount Currency object for check amount
     * @param serviceCharge Currency object for service charge
     * @returns boolean
     */
    private shouldIncludeProviderFee(
        inclProviderFee: any,
        checkAmount: Currency | null,
        serviceCharge: Currency | null
    ): boolean {
        return (
            inclProviderFee === 'Y' &&
            checkAmount != null &&
            serviceCharge != null &&
            checkAmount.getAmount() != null &&
            serviceCharge.getAmount() != null
        );
    }

    /**
     * Updates service charge and driver fee amounts by calling the wire-charges API.
     * Uses user-entered values if changed, otherwise uses original data.
     * @param wireCode ModelRow containing wire code data
     */
    private updateFees(wireCode: ModelRow) {
        let serviceCharge = null;
        let driverFee = null;

        // Get values from the data model
        const modelRow: ModelRow = this.mainDataSource.activeRow;
        const originalData = modelRow?.originalData
        const oldServiceCharge = originalData ? (originalData["service_charge"] as Currency) : undefined;
        const oldDriverFee = originalData ? (originalData["driver_fee_amt"] as Currency) : undefined;

        // Get values from the UI
        const userServiceCharge = modelRow?.getCurrency("service_charge");
        const userDriverFee = modelRow?.getCurrency("driver_fee_amt");

        // Check if user has updated the values
        const serviceChargeChanged = userServiceCharge?.amount !== oldServiceCharge?.amount;
        const driverFeeChanged = userDriverFee?.amount !== oldDriverFee?.amount;

        // If the user has updated the values, use them
        if (serviceChargeChanged) {
            serviceCharge = userServiceCharge;
        }
        if (driverFeeChanged) {
            driverFee = userDriverFee;
        }

        Api.search("lme/settlement/wire-charges",
            {
                check_amount: this.activeRow.get("check_amount"),
                wire_code: wireCode,
                service_charge: serviceCharge,
                driver_fee_amt: driverFee
            }
        ).then(response => {
            log.debug({ response });
            this.activeRow.set("service_charge", response.data[0].service_charge);
            this.activeRow.set("driver_fee_amt", response.data[0].driver_fee_amt);
            this.mainDataSource.displayDataInBoundComponents();
        });
    }

    showHideFieldsBySelectedWireType() {
        const selectedWireType = this.textboxWirecodeId.lookupModelData?.[0]?.get?.("wirecode.wire_type") ?? null;
        this.textboxFuelCardId.visible = this.textboxFuelCardId.required = WireType.ATM_CASH === selectedWireType
            || (WireType.INTERACTIVE_CHECK === selectedWireType
                && FuelInterface.MULTISERVICE === this.activeRow.get("fuel_interface_id"));
    }
}
